import { AbstractControl, ValidationErrors } from '@angular/forms';

export class GenericValidator {
  /**
   * Valida se o CPF é válido. Aceita CPF com ou sem máscara (pontos e traço).
   */
  static isValidCpf() {
    return (control: AbstractControl): ValidationErrors | null => {
      let cpf = control.value;

      if (cpf) {
        // Remove formatting (dots and dashes)
        cpf = cpf.replace(/[.-]/g, '');

        let numbers, digits, sum, i, result, equalDigits;
        equalDigits = 1;
        if (cpf.length < 11) {
          return null;
        }

        for (i = 0; i < cpf.length - 1; i++) {
          if (cpf.charAt(i) !== cpf.charAt(i + 1)) {
            equalDigits = 0;
            break;
          }
        }

        if (!equalDigits) {
          numbers = cpf.substring(0, 9);
          digits = cpf.substring(9);
          sum = 0;
          for (i = 10; i > 1; i--) {
            sum += numbers.charAt(10 - i) * i;
          }

          result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

          if (result !== Number(digits.charAt(0))) {
            return { invalidCpf: true };
          }
          numbers = cpf.substring(0, 10);
          sum = 0;

          for (i = 11; i > 1; i--) {
            sum += numbers.charAt(11 - i) * i;
          }
          result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

          if (result !== Number(digits.charAt(1))) {
            return { invalidCpf: true };
          }
          return null;
        } else {
          return { invalidCpf: true };
        }
      }
      return null;
    };
  }

  /**
   * Valida se o CNPJ é válido. Aceita CNPJ com ou sem máscara.
   * Formato: XX.XXX.XXX/XXXX-XX
   */
  static isValidCnpj() {
    return (control: AbstractControl): ValidationErrors | null => {
      let cnpj = control.value;

      if (!cnpj) {
        return null;
      }

      // Remove formatting (dots, slashes, and dashes)
      cnpj = cnpj.replace(/[./-]/g, '');

      // Check length - allow partial input during typing
      if (cnpj.length < 14) {
        return null;
      }

      // Reject if all digits are the same
      if (/^(\d)\1+$/.test(cnpj)) {
        return { invalidCnpj: true };
      }

      // Validate first check digit
      let sum = 0;
      const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      for (let i = 0; i < 12; i++) {
        sum += parseInt(cnpj.charAt(i)) * firstWeights[i];
      }
      let remainder = sum % 11;
      const firstDigit = remainder < 2 ? 0 : 11 - remainder;

      if (parseInt(cnpj.charAt(12)) !== firstDigit) {
        return { invalidCnpj: true };
      }

      // Validate second check digit
      sum = 0;
      const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      for (let i = 0; i < 13; i++) {
        sum += parseInt(cnpj.charAt(i)) * secondWeights[i];
      }
      remainder = sum % 11;
      const secondDigit = remainder < 2 ? 0 : 11 - remainder;

      if (parseInt(cnpj.charAt(13)) !== secondDigit) {
        return { invalidCnpj: true };
      }

      return null;
    };
  }

  /**
   * Valida se o valor é um CPF ou CNPJ válido.
   * Detecta automaticamente pelo tamanho: 11 dígitos = CPF, 14 dígitos = CNPJ.
   */
  static isValidCpfOrCnpj() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      // Remove all non-digit characters
      const digits = value.replace(/\D/g, '');

      // Allow partial input during typing
      if (digits.length < 11) {
        return null;
      }

      // CPF has 11 digits
      if (digits.length === 11) {
        const cpfValidator = GenericValidator.isValidCpf();
        return cpfValidator(control);
      }

      // CNPJ has 14 digits
      if (digits.length === 14) {
        const cnpjValidator = GenericValidator.isValidCnpj();
        return cnpjValidator(control);
      }

      // Between 11 and 14 digits - assume typing CNPJ, allow
      if (digits.length > 11 && digits.length < 14) {
        return null;
      }

      // More than 14 digits is invalid
      return { invalidCpfCnpj: true };
    };
  }
}
